import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, Save } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Field } from "./kit";
import BanglaInput from "./BanglaInput";
import BarcodeScanner from "./BarcodeScanner";

const EMPTY = { name: "", category: "", unit: "পিস", stock: "", rate: "", purchase_rate: "", company: "", barcode: "", code: "", note: "", low_stock: "5" };

export default function ProductForm({ initial, onSaved, onCancel, compact }: { initial?: any; onSaved: (p: any) => void; onCancel?: () => void; compact?: boolean }) {
  const { settings } = useSettings();
  const CATEGORIES = settings.categories;
  const UNITS = settings.units;
  const [f, setF] = useState<any>({ ...EMPTY, ...(initial || {}) });
  const [companies, setCompanies] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(false);
  const set = (k: string) => (e: any) => setF((o: any) => ({ ...o, [k]: e.target.value }));

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => null);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!String(f.name).trim()) {
      toast.error("পণ্যের নাম দিন");
      return;
    }
    setBusy(true);
    const body = { ...f, stock: +f.stock || 0, rate: +f.rate || 0, purchase_rate: +f.purchase_rate || 0, low_stock: +f.low_stock || 0 };
    try {
      const { data } = initial?.id ? await api.put(`/products/${initial.id}`, body) : await api.post("/products", body);
      toast.success(initial?.id ? "পণ্য আপডেট হয়েছে" : "পণ্য সেভ হয়েছে");
      onSaved(data);
      if (!initial?.id) setF({ ...EMPTY });
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="product-form">
      <div className={`grid gap-4 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
        <Field label="পণ্যের নাম *" className="sm:col-span-2 lg:col-span-1">
          <BanglaInput className="inp" value={f.name} onChange={(v) => setF((o: any) => ({ ...o, name: v }))} placeholder="যেমন: ২.৫ RM তার" testid="product-name-input" autoFocus />
        </Field>
        <Field label="ক্যাটাগরি">
          <input className="inp" list="re-cats" value={f.category} onChange={set("category")} placeholder="বাছাই বা লিখুন" data-testid="product-category-input" />
          <datalist id="re-cats">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
        <Field label="একক">
          <input className="inp" list="re-units" value={f.unit} onChange={set("unit")} data-testid="product-unit-input" />
          <datalist id="re-units">{UNITS.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
        <Field label="বিক্রয় দর (৳)">
          <input className="inp" type="number" step="any" min="0" value={f.rate} onChange={set("rate")} data-testid="product-rate-input" />
        </Field>
        <Field label="স্টক">
          <input className="inp" type="number" step="any" value={f.stock} onChange={set("stock")} data-testid="product-stock-input" />
        </Field>
        <Field label="কোম্পানি / ব্র্যান্ড">
          <input className="inp" list="re-comps" value={f.company} onChange={set("company")} data-testid="product-company-input" />
          <datalist id="re-comps">{companies.map((c) => <option key={c.id} value={c.name} />)}</datalist>
        </Field>
      </div>
      <details className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" open={!compact}>
        <summary className="text-sm font-semibold text-slate-600 cursor-pointer select-none">ঐচ্ছিক তথ্য (বারকোড, কোড, নোট)</summary>
        <div className={`grid gap-4 mt-3 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          <Field label="বারকোড">
            <div className="flex gap-2">
              <input className="inp" value={f.barcode} onChange={set("barcode")} placeholder="স্ক্যান করুন বা লিখুন" data-testid="product-barcode-input"
                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()} />
              <button type="button" className="btn btn-ghost px-3" onClick={() => setScan(true)} data-testid="product-barcode-camera-button" aria-label="ক্যামেরা স্ক্যান">
                <Camera size={18} />
              </button>
            </div>
          </Field>
          <Field label="পণ্য কোড">
            <input className="inp" value={f.code} onChange={set("code")} data-testid="product-code-input" />
          </Field>
          <Field label="ক্রয় দর (৳)">
            <input className="inp" type="number" step="any" min="0" value={f.purchase_rate} onChange={set("purchase_rate")} data-testid="product-purchase-rate-input" />
          </Field>
          <Field label="স্টক সতর্কতা (এর নিচে গেলে)">
            <input className="inp" type="number" step="any" min="0" value={f.low_stock} onChange={set("low_stock")} data-testid="product-low-stock-input" />
          </Field>
          <Field label="নোট" className="sm:col-span-2">
            <input className="inp" value={f.note} onChange={set("note")} data-testid="product-note-input" />
          </Field>
        </div>
      </details>
      <div className="flex justify-end gap-2">
        {onCancel && <button type="button" className="btn btn-ghost" onClick={onCancel} data-testid="product-form-cancel">বাতিল</button>}
        <button className="btn btn-primary" disabled={busy} data-testid="product-form-submit">
          <Save size={17} /> {busy ? "সেভ হচ্ছে..." : "সেভ করুন"}
        </button>
      </div>
      <BarcodeScanner open={scan} onClose={() => setScan(false)} onScan={(c) => { setScan(false); setF((o: any) => ({ ...o, barcode: c })); }} />
    </form>
  );
}
